/**
 * ShiftDuplicationModal - Pre-create multi-day shift duplication dialog
 * 
 * EF-DUPLICATION-PRECREATE-UX-ADJUSTMENT v1.1
 * 
 * Shown BEFORE entering Edit Shift form when creating first shift on multi-day project.
 * User can:
 * - "Duplicate Shift" - select dates and store plan for later execution
 * - "Single Shift" - proceed without duplication
 * - "Cancel" - close modal and don't open form
 */
import { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { ScrollArea } from "../ui/scroll-area";
import { Badge } from "../ui/badge";
import { Calendar, Copy, X, Check, ArrowRight } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * Generate all dates between start and end (inclusive)
 */
function getDateRange(startDate, endDate) {
  const dates = [];
  const current = new Date(startDate);
  const end = new Date(endDate);
  
  while (current <= end) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  
  return dates;
}

/**
 * Format date for display
 */
function formatDate(date) {
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
}

/**
 * Format date as ISO string (YYYY-MM-DD)
 */
function toISODateString(date) {
  return date.toISOString().split("T")[0];
}

export function ShiftDuplicationModal({
  isOpen,
  onClose,
  projectStartDate,
  projectEndDate,
  onConfirm,        // Called with dates when "Duplicate Shift" clicked
  onSingleShift,    // Called when "Single Shift" clicked
  isLoading = false,
}) {
  // Generate all project dates
  const allDates = useMemo(() => {
    if (!projectStartDate || !projectEndDate) return [];
    return getDateRange(projectStartDate, projectEndDate);
  }, [projectStartDate, projectEndDate]);

  // First date is where the shift will be created
  const firstDateIso = allDates.length > 0 ? toISODateString(allDates[0]) : "";

  // Selected dates (default: all dates AFTER the first one)
  const [selectedDates, setSelectedDates] = useState([]);

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen && allDates.length > 1) {
      // Pre-select all dates except the first one (where shift will be created)
      setSelectedDates(
        allDates.slice(1).map(d => toISODateString(d))
      );
    }
  }, [isOpen, allDates]);

  // Count of dates that will be duplicated (excluding first date)
  const duplicationCount = selectedDates.length;

  // Toggle date selection
  const toggleDate = (dateIso) => {
    setSelectedDates(prev => {
      if (prev.includes(dateIso)) {
        return prev.filter(d => d !== dateIso);
      } else {
        return [...prev, dateIso];
      }
    });
  };

  // Select all / deselect all (excluding first date)
  const handleSelectAll = () => {
    setSelectedDates(allDates.slice(1).map(d => toISODateString(d)));
  };

  const handleDeselectAll = () => {
    setSelectedDates([]);
  };

  // Handle "Duplicate Shift" confirm
  const handleConfirm = () => {
    if (selectedDates.length > 0) {
      onConfirm(selectedDates);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Multi-Day Project
          </DialogTitle>
          <DialogDescription>
            This is a multi-day project. Would you like to duplicate this shift across other days?
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          {/* Project date range info */}
          <div className="bg-surface-elevated/50 rounded-lg p-3 mb-4">
            <div className="text-sm text-muted-foreground">Project Duration</div>
            <div className="font-medium">
              {allDates.length > 0 && formatDate(allDates[0])} 
              {" → "}
              {allDates.length > 0 && formatDate(allDates[allDates.length - 1])}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              {allDates.length} day{allDates.length !== 1 ? "s" : ""}
            </div>
          </div>

          {/* Selection controls */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">
              Select dates to duplicate to:
            </span>
            <div className="flex gap-1">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleSelectAll}
                className="text-xs h-7"
              >
                All
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleDeselectAll}
                className="text-xs h-7"
              >
                None
              </Button>
            </div>
          </div>

          {/* Date list */}
          <ScrollArea className="h-[200px] border rounded-lg">
            <div className="p-2 space-y-1">
              {allDates.map((date, idx) => {
                const dateIso = toISODateString(date);
                const isFirst = idx === 0;
                const isSelected = selectedDates.includes(dateIso);
                
                return (
                  <div
                    key={dateIso}
                    className={cn(
                      "flex items-center gap-2 p-2 rounded-md transition-colors",
                      isFirst 
                        ? "bg-primary/10" 
                        : isSelected 
                          ? "bg-primary/5 hover:bg-primary/10" 
                          : "hover:bg-surface-elevated"
                    )}
                  >
                    <Checkbox
                      checked={isFirst ? true : isSelected}
                      disabled={isFirst}
                      onCheckedChange={() => !isFirst && toggleDate(dateIso)}
                    />
                    <span className={cn(
                      "flex-1 text-sm",
                      isFirst && "font-medium"
                    )}>
                      {formatDate(date)}
                    </span>
                    {isFirst && (
                      <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/30">
                        Create Here
                      </Badge>
                    )}
                    {!isFirst && isSelected && (
                      <Check className="w-4 h-4 text-primary" />
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          {/* Duplication count message */}
          {duplicationCount > 0 && (
            <div className="mt-3 text-sm text-center text-muted-foreground">
              This shift will be duplicated over{" "}
              <span className="font-medium text-primary">{duplicationCount}</span>{" "}
              additional day{duplicationCount === 1 ? "" : "s"}
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
          <Button 
            variant="outline" 
            onClick={onClose} 
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            <X className="w-4 h-4 mr-1" />
            Cancel
          </Button>
          <Button 
            variant="secondary"
            onClick={onSingleShift} 
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            <ArrowRight className="w-4 h-4 mr-1" />
            Single Shift
          </Button>
          <Button 
            onClick={handleConfirm} 
            disabled={duplicationCount === 0 || isLoading}
            className="w-full sm:w-auto shrink-0"
          >
            {isLoading ? (
              <>Duplicating...</>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-1" />
                Duplicate ({duplicationCount})
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ShiftDuplicationModal;
